import { Input, Select } from '@aigc/antd';
import { AliOssImage, NonEmpty } from '@aigc/components';
import { usePagingQueryHelper } from '@aigc/hooks';
import { useOverlay } from '@aigc/overlay-hook';
import type { Pagination } from '@aigc/types';
import { EyeOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Alert,
  Button,
  Form,
  Grid,
  Skeleton,
  Table,
  Tag,
  type TableColumnsType,
} from 'antd';
import { formatDuration, intervalToDuration } from 'date-fns';
import { zhCN } from 'date-fns/locale/zh-CN';
import { useImperativeHandle, useMemo, type Ref } from 'react';
import { useForm } from 'react-hook-form';
import { match } from 'ts-pattern';
import { object, string, type InferType } from 'yup';
import { FormControl } from '../../../components/form-control';
import { parsePaginationSearch } from '../../../features/common/pagination-search';
import { Permission } from '../../../features/common/permision';
import { ImageCredit } from '../../../features/credit/components/image-credit';
import { ImageDetailDrawer } from '../../../features/image/components/image-detail-drawer';
import { getImageTaskListQueryOptions } from '../../../features/task/api';
import { TaskStatus } from '../../../features/task/components/task-status';
import { TaskType, type ImageTaskDto } from '../../../features/task/model';
import { videoModelSpecs } from '../../../features/video-model/model-spec';

const querySchema = object({
  name: string().nullable(),
  type: string<TaskType.图片生成 | TaskType.图片翻译>().nullable(),
});

type QueryFormValues = InferType<typeof querySchema>;

export const RouteComponent = (props: {
  ref?: Ref<{ refresh: () => void }>;
}) => {
  const screen = Grid.useBreakpoint();
  const [openOverlay] = useOverlay();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const handlePaginationChange = (pagination: Pagination) => {
    navigate({
      replace: true,
      search: {
        page: pagination.page ?? 1,
        pageSize: pagination.size ?? 10,
      },
    });
  };

  const { control, handleSubmit, reset } = useForm({
    resolver: yupResolver(querySchema),
  });

  const { pagination, queryMany, queryParams, changePageNumber } =
    usePagingQueryHelper<QueryFormValues>({
      defaultPagination: { page: search.page, size: search.pageSize },
      defaultQueryParams: {
        name: '',
        type: null,
      },
      onRefresh: () => {
        refetch();
      },
    });

  const {
    data: history,
    isFetching,
    refetch,
  } = useQuery({
    ...getImageTaskListQueryOptions({
      ...pagination,
      name: queryParams?.name ?? void 0,
      type: queryParams?.type ?? void 0,
    }),
    placeholderData: keepPreviousData,
  });

  useImperativeHandle(props.ref, () => ({
    refresh: () => refetch(),
  }));

  const columns = useMemo(() => {
    const renderDetailButton = (task: ImageTaskDto) =>
      task.type === TaskType.图片生成 ? (
        <Button
          block
          variant="filled"
          color="default"
          icon={<EyeOutlined />}
          onClick={() => openOverlay(<ImageDetailDrawer task={task} />)}
        >
          查看详情
        </Button>
      ) : null;

    const _columns: TableColumnsType<ImageTaskDto> = [
      {
        className: '!pr-0',
        width: 96,
        render: (_, row) => {
          if (row.status !== 'succeeded' && row.status !== 'failed') {
            return (
              <Skeleton.Image classNames={{ content: '!w-20 !h-20' }} active />
            );
          }

          if (row.status === 'failed') {
            return <Skeleton.Image classNames={{ content: '!w-20 !h-20' }} />;
          }

          return match(row)
            .with({ type: TaskType.图片生成 }, (row) => (
              <AliOssImage size={80} src={row.filename}></AliOssImage>
            ))
            .with({ type: TaskType.图片翻译 }, (row) => (
              <AliOssImage size={80} src={row.result?.image_url}></AliOssImage>
            ))
            .otherwise(() => (
              <Skeleton.Image classNames={{ content: '!w-20 !h-20' }} />
            ));
        },
      },
      {
        render: (_, task) => {
          return (
            <div className="space-y-2">
              <div className="mb-2 flex flex-col items-start gap-2 sm:flex-row sm:items-center">
                <Tag># {task.id}</Tag>
                {match(task.type)
                  .with(TaskType.图片生成, () => <Tag>图片生成</Tag>)
                  .with(TaskType.图片翻译, () => <Tag>图片翻译</Tag>)
                  .otherwise(() => null)}
                <NonEmpty
                  value={task.generate_data?.name}
                  render={(value) => value}
                />
              </div>
              <div className="space-x-2">
                <TaskStatus task={task}></TaskStatus>
                <ImageCredit task={task}></ImageCredit>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                {match(task)
                  .with({ type: TaskType.图片生成 }, (task) => {
                    return (
                      <NonEmpty
                        value={task.generate_data?.aspect_ratio}
                        render={(value) => (
                          <span key="aspect_ratio">宽高比：{value}</span>
                        )}
                      ></NonEmpty>
                    );
                  })
                  .otherwise(() => null)}

                <NonEmpty
                  value={task.model}
                  render={(value) => (
                    <span key="model">
                      模型：{videoModelSpecs.get(value)?.name ?? value}
                    </span>
                  )}
                ></NonEmpty>
                <NonEmpty
                  value={task.created_at}
                  render={(value) => (
                    <span key="created_at">
                      创建时间： {new Date(value).toLocaleString()}
                    </span>
                  )}
                ></NonEmpty>
                {TaskStatus.isSettled(task.status) && (
                  <span className="text-xs">
                    耗时：
                    {formatDuration(
                      intervalToDuration({
                        start: task.created_at,
                        end: task.updated_at ?? 0,
                      }),
                      { locale: zhCN },
                    )}
                  </span>
                )}
              </div>
              {task.error && TaskStatus.isFailed(task.status) && (
                <Alert
                  classNames={{ title: 'break-words break-all' }}
                  type="error"
                  title={<span>{task.error}</span>}
                ></Alert>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-2 sm:hidden">
                {renderDetailButton(task)}
              </div>
            </div>
          );
        },
      },
    ];

    if (screen.sm) {
      _columns.push({
        width: 136,
        render: (_, task) => (
          <div className="flex flex-col gap-2">{renderDetailButton(task)}</div>
        ),
      });
    }

    return _columns;
  }, [openOverlay, screen.sm]);

  const onQuery = handleSubmit((values) => {
    queryMany({
      name: values.name,
      type: values.type,
    });
    handlePaginationChange({ page: 1, size: pagination.size });
  });

  return (
    <Table
      rowKey={(row) => row.id}
      showHeader={false}
      loading={isFetching}
      dataSource={history?.items}
      columns={columns}
      title={() => (
        <Form layout="inline">
          <FormControl
            control={control}
            name="name"
            label="名称"
            render={({ field }) => (
              <Input {...field} value={field.value ?? ''} />
            )}
          />
          <FormControl
            control={control}
            name="type"
            label="类型"
            render={({ field }) => (
              <Select
                {...field}
                value={field.value ?? undefined}
                allowClear
                placeholder="全部类型"
                className="min-w-32"
                options={[
                  { value: TaskType.图片生成, label: '图片生成' },
                  { value: TaskType.图片翻译, label: '图片翻译' },
                ]}
              />
            )}
          />
          <Button
            className="mr-2"
            onClick={onQuery}
            variant="solid"
            htmlType="submit"
            color="primary"
          >
            查询
          </Button>
          <Button
            variant="outlined"
            color="default"
            onClick={() => {
              reset({ name: '', type: null });
              queryMany({ name: '', type: null });
              handlePaginationChange({ page: 1, size: pagination.size });
            }}
          >
            重置
          </Button>
        </Form>
      )}
      pagination={{
        total: history?.total,
        simple: screen.xs,
        current: pagination.page,
        pageSize: pagination.size,
        onChange: (page, pageSize) => {
          changePageNumber(page, pageSize);
          handlePaginationChange({ page, size: pageSize });
        },
        showTotal: (total) => `共 ${total} 个记录`,
      }}
    ></Table>
  );
};

export const Route = createFileRoute('/_creator-center/tasks/image')({
  staticData: { permissions: [Permission.图片生成, Permission.图片翻译] },
  validateSearch: parsePaginationSearch,
  component: RouteComponent,
});
